/** Cliente explícito de S5.4; S7.2 decide quando ligá-lo à interface. */
import {
  STORE_LEADS,
  STORE_PLACES,
  completeListHydration,
  countRecords,
  getList,
  insertIfAbsent,
  isSealedKey,
  type LocalLead,
  type LocalList,
  type LocalPlace,
} from '../data/localDb';
import { apiRequest, unwrap } from './apiClient';
import { repairPlacePhones } from './normalizePlace';
import type { ApiResult } from '../types/api';
import type { ScanType } from '../types/searchList';

export const HYDRATION_PAGE_SIZE = 1000;

export interface ListBackupPageResponse {
  listId: string;
  module: ScanType;
  count: number;
  offset: number;
  items: unknown[];
}

export interface HydrateListBackupOptions {
  userId: string;
  signal?: AbortSignal;
  onProgress?: (progress: HydrateListBackupProgress) => void;
}

export interface HydrateListBackupProgress {
  listId: string;
  module: ScanType;
  loaded: number;
  total: number;
  inserted: number;
}

export interface HydrateListBackupResult {
  listId: string;
  module: ScanType;
  serverCount: number;
  localCount: number;
  inserted: number;
  /** O servidor ainda devolveu registros mascarados (leitura limitada do teste). */
  sealed?: boolean;
}

export type ListHydrationErrorCode =
  | 'invalid_hydration_page'
  | 'ownership_mismatch'
  | 'module_mismatch';

export class ListHydrationError extends Error {
  constructor(readonly code: ListHydrationErrorCode, message: string) {
    super(message);
    this.name = 'ListHydrationError';
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function isScanType(value: unknown): value is ScanType {
  return value === 'emails' || value === 'phones' || value === 'places' || value === 'hunter';
}

function invalid(message: string): never {
  throw new ListHydrationError('invalid_hydration_page', message);
}

function parsePage(
  payload: unknown,
  listId: string,
  requestedOffset: number,
  expectedModule: ScanType | undefined,
  previousCount: number | undefined,
): ListBackupPageResponse {
  const page = record(payload);
  if (!page) invalid('Resposta de hidratação inválida.');

  const responseListId = nonEmptyString(page['listId']);
  const module = page['module'];
  const count = page['count'];
  const offset = page['offset'];
  const items = page['items'];
  if (responseListId !== listId || !isScanType(module)) {
    invalid('Lista ou módulo divergente na resposta de hidratação.');
  }
  if (expectedModule && module !== expectedModule) {
    throw new ListHydrationError('module_mismatch', 'O módulo mudou entre páginas do backup.');
  }
  if (!Number.isSafeInteger(count) || Number(count) < 0 || offset !== requestedOffset) {
    invalid('Contagem ou offset inválido na resposta de hidratação.');
  }
  if (previousCount !== undefined && Number(count) < previousCount) {
    invalid('A contagem do backup diminuiu durante a paginação.');
  }
  if (!Array.isArray(items) || items.length > HYDRATION_PAGE_SIZE) {
    invalid('A página de hidratação excede o limite de 1.000 registros.');
  }
  if (requestedOffset + items.length > Number(count)) {
    invalid('A página contém mais registros que a contagem declarada.');
  }

  return {
    listId: responseListId,
    module,
    count: Number(count),
    offset: requestedOffset,
    items,
  };
}

function toLocalLead(value: unknown, listId: string): LocalLead {
  const candidate = record(value);
  if (!candidate) invalid('Lead inválido no backup.');
  const key = nonEmptyString(candidate['key']);
  const type = candidate['type'];
  const leadValue = nonEmptyString(candidate['value']);
  const createdAt = nonEmptyString(candidate['createdAt']);
  if (
    !key ||
    (type !== 'email' && type !== 'phone' && type !== 'profile') ||
    !leadValue ||
    !createdAt
  ) {
    invalid('Lead sem key, type, value ou createdAt válido.');
  }
  return {
    ...(candidate as Omit<LocalLead, 'listId'>),
    listId,
    key,
    type,
    value: leadValue,
    createdAt,
  };
}

function toLocalPlace(value: unknown, listId: string): LocalPlace {
  const candidate = record(value);
  if (!candidate) invalid('Place inválido no backup.');
  const key = nonEmptyString(candidate['key']);
  const cid = nonEmptyString(candidate['cid']);
  const createdAt = nonEmptyString(candidate['createdAt']);
  if (!key || !cid || !createdAt) invalid('Place sem key, cid ou createdAt válido.');
  const place: LocalPlace = {
    ...(candidate as Omit<LocalPlace, 'listId'>),
    listId,
    key,
    cid,
    createdAt,
  };
  // Backups e listas antigas podem trazer telefone sem a marca de celular (país não lido).
  return repairPlacePhones(place) ?? place;
}

function assertTarget(list: LocalList | undefined, userId: string, module?: ScanType): void {
  if (list && list.userId !== userId) {
    throw new ListHydrationError(
      'ownership_mismatch',
      'A lista local pertence a outra conta deste navegador.',
    );
  }
  if (list && module && list.module !== module) {
    throw new ListHydrationError('module_mismatch', 'O módulo local diverge do backup.');
  }
}

export function getListBackupPage(
  listId: string,
  offset: number,
  signal?: AbortSignal,
): Promise<ApiResult<ListBackupPageResponse>> {
  return apiRequest<ListBackupPageResponse>(
    `api/v2/lists/${encodeURIComponent(listId)}/leads?offset=${offset}`,
    { signal },
  );
}

/**
 * Baixa todas as páginas e insere cada uma sem sobrescrever. Registros parciais de uma falha
 * permanecem inertes se a lista ainda não existia: o marcador `LocalList` só nasce depois da
 * última página, e um retry reaproveita os registros já inseridos pela chave composta.
 */
export async function hydrateListBackup(
  listId: string,
  options: HydrateListBackupOptions,
): Promise<HydrateListBackupResult> {
  const userId = options.userId.trim();
  if (!userId) {
    throw new ListHydrationError('ownership_mismatch', 'A conta da hidratação é obrigatória.');
  }

  const initialList = await getList(listId);
  assertTarget(initialList, userId);

  let offset = 0;
  let module: ScanType | undefined;
  let serverCount: number | undefined;
  let inserted = 0;
  // Registro mascarado pelo servidor (teste): a lista é lida de novo depois da compra.
  let sealed = false;
  let reading = true;
  while (reading) {
    const response = unwrap(await getListBackupPage(listId, offset, options.signal));
    const page = parsePage(response, listId, offset, module, serverCount);
    module = page.module;
    serverCount = page.count;
    assertTarget(initialList, userId, module);

    if (module === 'places') {
      const places = page.items.map((item) => toLocalPlace(item, listId));
      sealed ||= places.some((place) => isSealedKey(place.key));
      inserted += await insertIfAbsent(STORE_PLACES, places);
    } else {
      const leads = page.items.map((item) => toLocalLead(item, listId));
      sealed ||= leads.some((lead) => isSealedKey(lead.key));
      inserted += await insertIfAbsent(STORE_LEADS, leads);
    }

    const loaded = await countRecords(module, listId);
    options.onProgress?.({
      listId,
      module,
      loaded,
      total: Math.max(page.count, loaded),
      inserted,
    });

    const nextOffset = offset + page.items.length;
    if (nextOffset >= page.count) {
      reading = false;
    } else {
      if (page.items.length === 0) invalid('O backup terminou antes da contagem declarada.');
      offset = nextOffset;
    }
  }

  if (!module || serverCount === undefined) invalid('O backup não devolveu a primeira página.');
  const completed = await completeListHydration({
    id: listId,
    userId,
    module,
    hydratedAt: new Date().toISOString(),
    ...(sealed ? { sealed: true } : {}),
  });
  if (!completed.ok) {
    throw new ListHydrationError(completed.reason, 'A lista local divergiu durante a hidratação.');
  }

  return {
    listId,
    module,
    serverCount,
    localCount: completed.count,
    inserted,
    ...(sealed ? { sealed: true } : {}),
  };
}
